import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { cn } from "@/lib/utils";
import type { MentionMember } from "@/lib/mentions";

/**
 * MentionTextarea — a plain textarea plus an "@" autocomplete popup for
 * workspace members. Reused by the comment composer and the reply composer.
 *
 * It stays DISPLAY-only: the value it holds/emits is plain text with "@Name"
 * (never the @[Name](u:id) storage token). The parent converts to tokens at
 * submit via convertMentionsToTokens(value, members). Pass `members={[]}` (or
 * the empty result of a disabled query) to get a no-op plain textarea — that's
 * how guest/share mode falls back with zero autocomplete.
 *
 * Keyboard:
 *   - popup open : ↑/↓ move, Enter/Tab pick, Esc close (all preventDefault'd)
 *   - popup closed : Enter → onSubmitKey(), Shift+Enter → newline, Esc → onEscape()
 */

type Props = {
  value: string;
  onChange: (v: string) => void;
  members: MentionMember[];
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onSubmitKey?: () => void;
  /** Called on Escape when the suggestion popup is closed (e.g. cancel a reply). */
  onEscape?: () => void;
};

const MAX_SUGGESTIONS = 8;

type MentionCtx = { start: number; query: string } | null;

/** Find an active "@query" ending at the caret, or null. Allows spaces so
 *  multi-word names match; bailout keeps it from scanning the whole line. */
function detectMention(text: string, caret: number): MentionCtx {
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === "\n") return null;
    if (ch === "@") {
      const prev = i > 0 ? text[i - 1] : "";
      if (prev === "" || /[^\w@]/.test(prev)) {
        const query = text.slice(i + 1, caret);
        if (!query.includes("\n")) return { start: i, query };
      }
      return null;
    }
    if (caret - i > 40) return null; // query too long to be a name
    i--;
  }
  return null;
}

export const MentionTextarea = forwardRef<HTMLTextAreaElement, Props>(
  function MentionTextarea(
    { value, onChange, members, placeholder, rows = 3, className, disabled, autoFocus, onSubmitKey, onEscape },
    forwardedRef,
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement, []);

    const [ctx, setCtx] = useState<MentionCtx>(null);
    const [active, setActive] = useState(0);

    const suggestions =
      ctx && members.length
        ? members
            .filter((m) =>
              m.name.toLowerCase().startsWith(ctx.query.toLowerCase()),
            )
            .slice(0, MAX_SUGGESTIONS)
        : [];
    const open = suggestions.length > 0;

    function refreshCtx(el: HTMLTextAreaElement) {
      if (!members.length) {
        setCtx(null);
        return;
      }
      const next = detectMention(el.value, el.selectionStart ?? el.value.length);
      setCtx(next);
      setActive(0);
    }

    function pick(member: MentionMember) {
      if (!ctx) return;
      const el = innerRef.current;
      const caret = el?.selectionStart ?? value.length;
      const before = value.slice(0, ctx.start);
      const after = value.slice(caret);
      const insert = `@${member.name} `;
      const nextValue = before + insert + after;
      onChange(nextValue);
      setCtx(null);
      // Restore caret just past the inserted mention.
      const nextCaret = before.length + insert.length;
      requestAnimationFrame(() => {
        const node = innerRef.current;
        if (node) {
          node.focus();
          node.setSelectionRange(nextCaret, nextCaret);
        }
      });
    }

    function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
      if (open) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActive((a) => (a + 1) % suggestions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          pick(suggestions[active] ?? suggestions[0]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setCtx(null);
          return;
        }
      }
      // Popup closed → Enter sends, Shift+Enter inserts a newline (default),
      // Escape cancels. Guard isComposing so confirming an IME candidate with
      // Enter doesn't fire a send.
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        onSubmitKey?.();
      } else if (e.key === "Escape" && onEscape) {
        e.preventDefault();
        onEscape();
      }
    }

    return (
      <div className="relative">
        {open && (
          <ul
            role="listbox"
            className="absolute bottom-full left-0 z-30 mb-1 max-h-56 w-64 overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
          >
            {suggestions.map((m, idx) => (
              <li key={m.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={idx === active}
                  // onMouseDown (not onClick) so the textarea doesn't blur first.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(m);
                  }}
                  onMouseEnter={() => setActive(idx)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
                    idx === active
                      ? "bg-secondary text-foreground"
                      : "text-foreground/80 hover:bg-secondary/60",
                  )}
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/15 text-[11px] font-semibold text-brand">
                    {initials(m.name)}
                  </span>
                  <span className="truncate">{m.name}</span>
                  {m.role && (
                    <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {roleLabel(m.role)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <textarea
          ref={innerRef}
          value={value}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          rows={rows}
          className={className}
          onChange={(e) => {
            onChange(e.target.value);
            refreshCtx(e.target);
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={(e) => refreshCtx(e.currentTarget)}
          onClick={(e) => refreshCtx(e.currentTarget)}
          onBlur={() => setCtx(null)}
        />
      </div>
    );
  },
);

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function roleLabel(role: string): string {
  switch (role) {
    case "ADMIN":
      return "Owner";
    case "FULL_ACCESS":
      return "Full";
    case "EDIT_SHARE":
      return "Share";
    case "EDIT":
      return "Edit";
    default:
      return role;
  }
}
